/**
 * meshPipeline — animated network graph.
 *
 * N nodes drift slowly through 2D space. Each node connects to its
 * K nearest neighbors. Lines fade in based on proximity and audio.
 * Nodes are drawn as soft glowing points, edges as thin lines.
 *
 * This is a unique visual — it looks like a constellation map, a
 * circuit diagram, or a constellation of stars connected by gravity.
 * The graph structure is implicit (each node finds its neighbors
 * each frame via a sorted distance comparison), not pre-baked.
 *
 * Audio:
 *   - bass: node drift speed
 *   - mid:  edge visibility
 *   - high: node brightness
 *
 * Single fullscreen quad fragment shader. No GPU compute.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MeshPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Position of node i at time t. The node drifts in a small lissajous
// orbit around its initial seed position.
vec2 nodePos(float i, float t) {
  vec2 seed = vec2(
    hash21(vec2(i, 1.0)) * 2.0 - 1.0,
    hash21(vec2(i, 2.0)) * 2.0 - 1.0
  );
  float phaseX = i * 1.3;
  float phaseY = i * 0.7 + 1.1;
  // Scaled drift — amplitude depends on bass.
  float drift = 0.08 + 0.06 * sin(i * 0.5);
  vec2 offset = vec2(
    sin(t * (0.4 + 0.1 * sin(i))) + phaseX,
    cos(t * (0.5 + 0.07 * cos(i * 1.1))) + phaseY
  ) * drift;
  // Scale seed into screen coords.
  return seed * 0.85 + offset;
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background: deep dark with subtle radial glow.
  vec3 bg = vec3(0.02, 0.03, 0.08);
  // Subtle vignette darkening at the edges.
  float r = length(p);
  bg *= 1.0 - smoothstep(0.5, 1.0, r) * 0.5;

  vec3 col = bg;

  // Number of nodes.
  const int N = 32;
  // K nearest neighbors to connect.
  const int K = 3;

  // Pre-compute all node positions (constant memory tradeoff).
  vec2 nodePositions[32];
  for (int i = 0; i < 32; i++) {
    if (i < N) {
      nodePositions[i] = nodePos(float(i), uTime);
    } else {
      nodePositions[i] = vec2(100.0);  // off-screen
    }
  }

  // Edge threshold distance — shorter edges are brighter.
  float edgeMaxDist = 0.55;

  // For each pair of nodes (i, j), draw an edge if i < j.
  for (int i = 0; i < 32; i++) {
    if (i >= N) break;
    vec2 pi = nodePositions[i];
    for (int j = i + 1; j < 32; j++) {
      if (j >= N) break;
      vec2 pj = nodePositions[j];
      // Distance between this edge and the current pixel.
      vec2 ab = pj - pi;
      float abLen = length(ab);
      // Only draw edges shorter than the threshold.
      if (abLen > edgeMaxDist) continue;
      // Closest distance from p to the line segment pi-pj.
      float t = clamp(dot(p - pi, ab) / (abLen * abLen + 1e-5), 0.0, 1.0);
      vec2 closest = pi + ab * t;
      float dLine = length(p - closest);
      // Edge color.
      vec3 edgeColor = vec3(0.3, 0.5, 0.9);
      // Audio: edges pulse with mid. Pulse phase from node id.
      float pulse = 0.4 + 0.6 * mid + 0.4 * sin(uTime * 2.0 + float(i) * 0.7);
      // Line thickness and length factor.
      float thickness = 0.0025;
      float line = smoothstep(thickness, 0.0, dLine);
      // Length attenuation: shorter edges are brighter.
      float lengthFactor = 1.0 - abLen / edgeMaxDist;
      col += edgeColor * line * lengthFactor * pulse;
    }
  }

  // Render nodes as glowing points.
  for (int i = 0; i < 32; i++) {
    if (i >= N) break;
    vec2 np = nodePositions[i];
    float d = length(p - np);
    // Soft glow.
    float radius = 0.012 + high * 0.008;
    float glow = exp(-d * d / (radius * radius));
    // Per-node color (rainbow).
    float hue = float(i) / float(N);
    vec3 nodeColor = vec3(
      0.5 + 0.5 * sin(hue * 6.2831853 + 0.0),
      0.5 + 0.5 * sin(hue * 6.2831853 + 2.094),
      0.5 + 0.5 * sin(hue * 6.2831853 + 4.189)
    );
    // Mix toward cyan-white for sci-fi feel.
    nodeColor = mix(nodeColor, vec3(0.7, 0.9, 1.0), 0.3);
    float brightness = 0.5 + high * 0.8 + 0.3 * bass;
    col += nodeColor * glow * brightness;
    // Subtle inner bright core.
    float core = exp(-d * d * 5000.0);
    col += vec3(1.0) * core * 0.5;
  }

  // Tone mapping (ACES-approximation).
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeMeshBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeMeshRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMeshPipeline(gl: WebGL2RenderingContext): MeshPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMesh(
  gl: WebGL2RenderingContext,
  pipe: MeshPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
